/**
 * "Have the AI draft this" for one long-text field.
 *
 * Deliberately a two-step: the admin describes their domain in a sentence, sees
 * what came back, and chooses to use it. Nothing is written into the form until
 * they accept — a drafted prompt they didn't read is worse than a blank box,
 * because it looks considered.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Banner, Button, Input } from '@servicecompressionllc/sc-design-system';
import { knowledgeApi } from './api.js';

export const DraftWithAi = ({
  kind,
  label,
  gistPlaceholder,
  categoryName,
  briefing,
  subjectNoun,
  itemNoun,
  onAccept,
}: {
  kind: 'briefing' | 'list' | 'item';
  label: string;
  gistPlaceholder: string;
  categoryName?: string;
  /** Passed for stage drafts so the draft knows the domain, not just the nouns. */
  briefing?: string;
  subjectNoun?: string;
  itemNoun?: string;
  onAccept: (text: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [gist, setGist] = useState('');

  const draft = useMutation({
    mutationFn: () =>
      knowledgeApi.draftPrompt({
        kind,
        gist: gist.trim(),
        categoryName,
        briefing,
        subjectNoun,
        itemNoun,
      }),
  });

  if (!open) {
    return (
      <div className="mb-4">
        <Button variant="secondary" onClick={() => setOpen(true)}>
          {label}
        </Button>
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-lg border border-outline p-3">
      <label className="block text-sm font-medium text-ink">
        Describe it in a sentence or two
        <Input
          value={gist}
          aria-label="Describe it"
          placeholder={gistPlaceholder}
          onChange={(e) => setGist(e.target.value)}
          className="mt-1 w-full"
        />
      </label>

      {draft.data && (
        <div className="mt-3">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-subtle">
            Draft — read it before using it
          </p>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded border border-outline bg-surface p-2 text-xs text-ink">
            {draft.data.text}
          </pre>
        </div>
      )}
      {draft.error && (
        <Banner variant="danger" className="mt-3">
          <Banner.Description>{(draft.error as Error).message}</Banner.Description>
        </Banner>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          onClick={() => draft.mutate()}
          disabled={draft.isPending || gist.trim().length < 3}
        >
          {draft.isPending ? 'Drafting…' : draft.data ? 'Try again' : 'Draft it'}
        </Button>
        {draft.data && (
          <Button
            onClick={() => {
              onAccept(draft.data.text);
              setOpen(false);
            }}
          >
            Use this
          </Button>
        )}
        <Button variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
};
